/*
  React Flow Transform Engine

  This module converts Journey Document Model data to React Flow format.
  It handles the transformation of nodes and edges from the normalized
  document format to the format expected by React Flow.
*/

import { v4 as uuidv4 } from 'uuid';
import type { Node as ReactFlowNode, Edge as ReactFlowEdge } from 'react-flow-renderer';
import type { JourneyDocument, OutcomeNode, JourneyWorkflowMetadata } from '../types';
import { convertDocNodeToReactFlowNode } from './reactFlow/nodeConverter';
import { convertDocEdgeToReactFlowEdge } from './reactFlow/edgeConverter';
import { DEFAULT_EDGE_STYLE } from './reactFlow/constants';
import { generateTerminalReconSynthetics } from './terminalReconSynthetics';

/**
 * React Flow graph structure
 */
export interface ReactFlowGraph {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
}

/**
 * Validate that nodes and edges are properly connected
 *
 * This function filters out invalid edges that reference non-existent nodes.
 *
 * @param nodes - Array of React Flow nodes
 * @param edges - Array of React Flow edges
 * @returns Filtered edges that have valid source and target nodes
 */
function validateGraphConnections(
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[],
): ReactFlowEdge[] {
  return edges.filter(edge => edge.source
    && edge.target
    && nodes.some(n => n.id === edge.source)
    && nodes.some(n => n.id === edge.target),
  );
}

/**
 * Generate synthetic edges from terminal outcome nodes to reconciliation workflow node
 *
 * These edges are purely for visualization in React Flow and should not be persisted
 * back to the API. They help show the connection from terminal outcomes to the
 * reconciliation workflow node.
 *
 * @param document - The journey document
 * @returns Array of synthetic React Flow edges
 */
function generateReconciliationWorkflowEdges(
  document: JourneyDocument,
): ReactFlowEdge[] {
  const syntheticEdges: ReactFlowEdge[] = [];

  // Check if there's a terminal reconciliation workflow node configured
  const reconNodeId = document.terminal_reconciliation_workflow_node_id;
  if (!reconNodeId || !document.nodes[reconNodeId]) {
    return syntheticEdges;
  }

  // Verify the node is actually a reconciliation_workflow type
  const reconNode = document.nodes[reconNodeId];
  if (reconNode.type !== 'reconciliation_workflow') {
    return syntheticEdges;
  }

  // Find all leaf outcome nodes (outcomes with no outgoing edges)
  // These are terminal outcomes that should connect to the reconciliation workflow
  const terminalOutcomeNodes = Object.values(document.nodes).filter((node): node is OutcomeNode => {
    if (node.type !== 'outcome') return false;

    // Check if it has no outgoing edges (it's a leaf node)
    const hasOutgoingEdge = Object.values(document.edges).some(
      edge => edge.from.node === node.id,
    );

    // Only include outcome nodes that are leaf nodes (no outgoing edges)
    return !hasOutgoingEdge;
  });

  // Create synthetic edges from each terminal outcome to the reconciliation workflow
  terminalOutcomeNodes.forEach(outcomeNode => {
    const syntheticEdge: ReactFlowEdge = {
      id: `synthetic-recon-${uuidv4()}`,
      source: outcomeNode.id,
      target: reconNodeId,
      type: 'straight',
      animated: false,
      sourceHandle: 'source',
      targetHandle: 'target',
      style: { ...DEFAULT_EDGE_STYLE },
      // Mark this edge as synthetic so it can be identified if needed
      data: {
        isSyntheticReconciliationEdge: true,
      },
    };

    syntheticEdges.push(syntheticEdge);
  });

  return syntheticEdges;
}

/**
 * Main transformation function: Convert Journey Document to React Flow format
 *
 * This function orchestrates the complete transformation from the normalized
 * document model to React Flow format. It performs:
 * 1. Node conversion to React Flow format
 * 2. Edge conversion to React Flow format
 * 3. Generation of synthetic edges for legacy reconciliation workflow visualization
 * 4. Generation of synthetic nodes and edges for non-legacy terminal recon workflows
 * 5. Validation of graph connections
 *
 * @param document - The journey document to transform
 * @param workflows - Optional workflow metadata with cached workflow data (for terminal recon synthetics)
 * @returns A React Flow graph ready for rendering
 */
export function convertDocumentToReactFlow(
  document: JourneyDocument,
  workflows?: Record<string, JourneyWorkflowMetadata>,
): ReactFlowGraph {
  // Convert nodes
  const nodes = Object.values(document.nodes).map(convertDocNodeToReactFlowNode);

  // Convert edges from document
  const documentEdges = Object.values(document.edges).map(
    edge => convertDocEdgeToReactFlowEdge(edge),
  );

  // Generate synthetic edges for legacy reconciliation workflow visualization
  // These edges connect terminal outcome nodes to the legacy reconciliation workflow node
  // and are purely for display purposes - they won't be saved back to the API
  const syntheticReconciliationEdges = generateReconciliationWorkflowEdges(document);

  // Generate synthetic nodes and edges for non-legacy terminal recon workflows
  // These show the outcomes from the terminal recon workflow and are also purely for display
  let syntheticTerminalReconNodes: ReactFlowNode[] = [];
  let syntheticTerminalReconEdges: ReactFlowEdge[] = [];

  if (workflows) {
    const terminalReconSynthetics = generateTerminalReconSynthetics(document, workflows);
    syntheticTerminalReconNodes = terminalReconSynthetics.nodes;
    syntheticTerminalReconEdges = terminalReconSynthetics.edges;
  }

  // Combine all nodes and edges
  const allNodes = [...nodes, ...syntheticTerminalReconNodes];
  const allEdges = [
    ...documentEdges,
    ...syntheticReconciliationEdges,
    ...syntheticTerminalReconEdges,
  ];

  // Filter out any invalid nodes or edges
  const validNodes = allNodes.filter(node => node.id && node.type);
  const validEdges = validateGraphConnections(validNodes, allEdges);

  return {
    nodes: validNodes,
    edges: validEdges,
  };
}
